import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import GASidebar from "@components/tables/GAAdmin/GASidebar";
import { BASE_URL, gender, ROLE_ROUTES, salutations, securityQuestions, validRoles } from "@utils/constants";
import { fetchSentencesForGAAdmin } from "@store/thunks/gaadminThunk";

const GACSAdminLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, userId, token } = useSelector((store) => store.auth);
  const { selectedAssignedFileId, fileStatus, targetLanguageId } = useSelector((store) => store.gaAdmin);
  const [showRejected, setShowRejected] = useState(false);
  const [loading, setLoading] = useState(false);

  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;
  const isProjectPage = location.pathname === destinationRoute || location.pathname === `${destinationRoute}/`;
  const isFolderPage = location.pathname === `${destinationRoute}/folders`;
  const isFilePage = location.pathname === `${destinationRoute}/folders/files`;
  const isManageUsersPage = location.pathname === `${destinationRoute}/manage-users`;
  const isRegistrationPage = location.pathname === `${destinationRoute}/register`;
  const isSentencesPage = location.pathname.includes(`${destinationRoute}/folders/files/sentences`);

  const headerTitle = isSentencesPage
    ? <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Dashboard Sentences</span>
    : isProjectPage
      ? <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Dashboard Projects</span>
      : isFolderPage
        ? <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Dashboard Folders</span>
        : isFilePage
          ? <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Dashboard Files</span>
          : isManageUsersPage
            ? <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Manage Users</span>
            : isRegistrationPage
              ? <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Register User Page</span>
              : <span><span className="text-[#ec7026] shadow-sm">GA Admin</span> Dashboard</span>;

  const headerSubtitle = isSentencesPage
    ? "Review and manage sentences from the file"
    : isProjectPage
      ? "Manage your GA projects and settings"
      : isFolderPage
        ? "Manage your GA folders and settings"
        : isFilePage
          ? "Manage your GA files and settings"
          : isManageUsersPage
            ? "Manage your GA users and settings"
            : "Manage your GA projects and settings";

  const handleClickHome = () => {
    navigate(destinationRoute);
  };

  const handleClickBack = () => {
    navigate(-1);
  };

  const handleManageUsers = () => {
    navigate("manage-users");
  };

  const registerButton = () => {
    navigate('/GACSAdmin/register')
  }



  useEffect(() => {
    if (isSentencesPage && selectedAssignedFileId && fileStatus && targetLanguageId) {
      setLoading(true);
      dispatch(fetchSentencesForGAAdmin(selectedAssignedFileId, fileStatus, targetLanguageId))
        .finally(() => setLoading(false));
    }
  }, [dispatch, isSentencesPage, selectedAssignedFileId, fileStatus, targetLanguageId]);

  return (
    <div className="flex min-h-screen bg-gray-50/50">
      <div className="fixed w-1/6 h-screen bg-white border-r border-gray-200 z-20">
        <GASidebar />
      </div>
      <div className="flex-1 flex flex-col ml-[16.666%]">
        <div className="fixed top-16 left-[16.666%] w-[83.334%] bg-white border-b border-gray-200 shadow-sm z-30">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
              <div className="flex flex-col">
                <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                  {headerTitle}
                </h1>
                <p className="text-sm font-['Semantic/Strong'] text-gray-500">
                  {headerSubtitle}
                </p>
              </div>
              <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                <button
                  onClick={handleClickHome}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  <span>Home</span>
                </button>
                <button
                  onClick={handleClickBack}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  <span>Back</span>
                </button>
                {isManageUsersPage && <>
                  <button
                    onClick={registerButton}
                    className={`inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:cursor-pointer hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200 ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <svg
                      className="w-4 h-4 mr-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                    <span>Register User</span>
                  </button>

                </>}
                {isProjectPage && (
                  <>
                    <button
                      onClick={handleManageUsers}
                      className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                    >
                      <svg
                        className="w-4 h-4 mr-1.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                      <span>Manage Users</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 w-full overflow-y-auto p-4 sm:p-6 lg:p-8 pt-[144px] mt-28">
          <Outlet context={{ showRejected, loading }} />
        </div>
      </div>
    </div>
  );
};

export default GACSAdminLayout;